import * as RadixDialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import type { ReactNode } from 'react'

// TailAdmin Modal (src/components/ui/modal): gray-400/50 blurred backdrop, rounded-3xl white panel,
// round gray-100 close button top-right. Radix supplies the focus trap, Esc-to-close and
// focus-return to the trigger.

const WIDTHS = { sm: 'max-w-[440px]', md: 'max-w-[584px]', lg: 'max-w-[700px]', xl: 'max-w-[1000px]' } as const

export function Dialog({ open, onOpenChange, title, size = 'md', children, footer }: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  size?: keyof typeof WIDTHS
  children: ReactNode
  footer?: ReactNode
}) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-[99999] bg-gray-400/50 backdrop-blur-[32px]" />
        <RadixDialog.Content
          className={`fixed left-1/2 top-1/2 z-[99999] -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] ${WIDTHS[size]} max-h-[90vh] flex flex-col rounded-3xl bg-white focus:outline-none`}
        >
          <RadixDialog.Close asChild>
            <button
              aria-label="Close"
              className="absolute right-3 top-3 z-[999] flex h-[38px] w-[38px] items-center justify-center rounded-full bg-gray-100 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-700 sm:right-6 sm:top-6 sm:h-11 sm:w-11"
            >
              <X size={22} />
            </button>
          </RadixDialog.Close>
          <div className="px-6 pt-6 pr-16 lg:px-10 lg:pt-10 lg:pr-20">
            <RadixDialog.Title className="text-2xl font-semibold text-gray-800">{title}</RadixDialog.Title>
          </div>
          <div className="overflow-y-auto custom-scrollbar px-6 pb-6 pt-6 lg:px-10 lg:pb-10">{children}</div>
          {footer && <div className="flex items-center justify-end gap-3 px-6 pb-6 lg:px-10 lg:pb-10">{footer}</div>}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  )
}

// Uncontrolled convenience wrapper matching Modal.tsx's call shape (mount-to-show,
// unmount-to-hide) so existing call sites keep working unchanged.
export function SimpleDialog({ title, onClose, size, children }: {
  title: string
  onClose: () => void
  size?: keyof typeof WIDTHS
  children: ReactNode
}) {
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose() }} title={title} size={size}>
      {children}
    </Dialog>
  )
}
